import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    FormControl,
    IconButton,
    Stack,
    useTheme,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    alpha,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
} from "@mui/material";
import { Close, NotInterested } from "@mui/icons-material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DevicesIcon from '@mui/icons-material/Devices';
import DnsIcon from '@mui/icons-material/Dns';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";
import { AppId } from "../../../app/appTypes";
import { ValidationProcessStepHeader } from "./components/ValidationProcessStepHeader";
import { createValidationProcess, CreateValidationProcessPayload, ProcessTeamMember, UrsStageMode, ValidationObjectType, ValidationScenario } from "./validationProcessApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { EntityId, unwrapResult } from "@reduxjs/toolkit";
import { ProjectProcess } from "../projectProcess/ProjectProcessApiClient";
import { DeviceSerialNoField } from "../../device/DeviceSerialNoField";
import { Localized } from "@fluent/react";
import { StructureName } from "../../device/Structure";
import { DeviceSelectField } from "../../device/DeviceSelectField";
import { ValidationTeamEditor } from "./components/ValidationTeamEditor";
import { selectLoggedUser } from "../../user/userSlice";
import { DocumentSelectField } from "../../documents/controls/DocumentSelectField";
import { Device } from "../../device/deviceSlice";
import ProcessNumberField from "../projectProcess/components/ProcessNumberField";
import { DocumentHeader } from "../../documents/documentsApi";
import { selectStructureNodeById } from "../../device/structureSlice";
import { ComputerizedSystemSelectField } from "../../computerizedSystems/ComputerizedSystemSelectField";
import { selectSettings } from "../../settings/settingsSlice";
import { loadDeviceDetails } from "../../device/deviceSlice";


const steps = [
    "Typ walidowanego obiektu",
    "Scenariusz walidacji",
    "Dane obiektu",
    "Dane procesu",
    "URS",
    "Podsumowanie"
];

const PROCESS_TYPE_LABEL: Record<ValidationObjectType, string> = {
    DEVICE: "Urządzenie",
    COMPUTERIZED_SYSTEM: "System skomputeryzowany",
    ROOM: "Pomieszczenie",
};

const SCENARIO_LABEL: Record<ValidationScenario, string> = {
    NEW: "Nowa walidacja",
    REQUAL_PERIODIC: "Rekwalifikacja okresowa",
    REQUAL_CHANGE: "Rekwalifikacja po zmianach",
};

const choiceCard = (selected: boolean) => ({
    position: 'relative',
    p: 1,
    border: 1.5,
    borderColor: selected ? "success.main" : "divider",
    borderRadius: 2,
    cursor: "pointer",
    bgcolor: "background.paper",
    transition: "border-color .15s ease, transform .05s ease",
    minHeight: 64,
    "&:hover": {
        borderColor: selected ? "success.main" : "text.disabled",
        bgcolor: "background.paper",
    },

});

export const buildProcessTitle = (
    lang: string,
    name?: string,
    serial?: string
) => {
    const n = (name ?? "").trim();
    const sn = (serial ?? "").trim();

    const isEN = (lang ?? "en") === "en";
    const t = isEN
        ? { base: "Validation process", for: "for", sn: "S/N" }
        : { base: "Proces walidacyjny", for: "dla", sn: "S/N" };

    if (n && sn) return `${t.base} ${t.for} "${n}" (${t.sn} ${sn})`;
    if (n) return `${t.base} ${t.for} "${n}"`;
    if (sn) return `${t.base} (${t.sn} ${sn})`;
    return t.base;
};

type ValidationProcessWizardProps = {
    onSave: (process: ProjectProcess) => void;
    onClose: () => void;
    initialDeviceId?: AppId | null;
    projectId?: AppId | number | null;
};

const ValidationProcessWizard: React.FC<ValidationProcessWizardProps> = ({ onSave, onClose, initialDeviceId, projectId }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const loggedUser = useAppSelector(selectLoggedUser)
    const { locale } = useAppSelector(selectSettings);
    const lang = locale === "pl" ? "pl" : "en";
    const isValidatedObjectLockedToDevice = initialDeviceId != null;

    const [activeStep, setActiveStep] = useState(0);

    const [validatedObjectType, setValidatedObjectType] = useState<ValidationObjectType | null>(null);
    const [scenario, setScenario] = useState<ValidationScenario | null>(null);

    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(undefined);
    const [newDeviceName, setNewDeviceName] = useState("");
    const [serialNo, setSerialNo] = useState("");
    const [structureId, setStructureId] = useState<AppId | undefined>(undefined);

    const [computerizedSystemId, setComputerizedSystemId] = useState("");
    const [computerizedSystem, setComputerizedSystem] = useState<any | undefined>(undefined);

    const [serialValidationOk, setSerialValidationOk] = useState(false);

    const [processNumber, setProcessNumber] = useState("");
    const [processTitle, setProcessTitle] = useState("");
    const [skipUrsComment, setSkipUrsComment] = useState("");
    const [processTitleDirty, setProcessTitleDirty] = useState(false);

    const [isProcessNumberValid, setIsProcessNumberValid] = useState(false);

    const [selectedUrsId, setSelectedUrsId] = useState<string | null>(null);
    const [selectedUrsDoc, setSelectedUrsDoc] = useState<DocumentHeader | null>(null);

    const [objectOption, setObjectOption] = useState<"EXISTING" | "NEW" | "">("");
    const [ursMode, setUrsMode] = useState<UrsStageMode | "">("");


    const codeStructureId: EntityId | undefined = React.useMemo(() => {
        if (validatedObjectType === "COMPUTERIZED_SYSTEM") {
            return computerizedSystem?.structureId != null
                ? String(computerizedSystem.structureId)
                : undefined;
        }

        if (validatedObjectType === "DEVICE") {
            if (objectOption === "NEW") {
                return structureId != null ? String(structureId) : undefined;
            }
            return selectedDevice?.structureId != null
                ? String(selectedDevice.structureId)
                : undefined;
        }

        // ROOM lub brak wyboru – dopasuj, gdy dodasz obsługę
        return undefined;
    }, [
        validatedObjectType,
        objectOption,
        structureId,
        selectedDevice?.structureId,
        computerizedSystem?.structureId,
    ]);

    const codeStructureNode = useAppSelector((state) =>
        codeStructureId === undefined ? undefined : selectStructureNodeById(state, codeStructureId)
    );

    const structureCode = codeStructureNode?.code;

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const isLastStep = activeStep === steps.length - 1;

    const handleDeviceTypeIdChange = useCallback(structureId => setStructureId(structureId), []);
    const [projectTeam, setProjectTeam] = useState<{ manager: string | null; members: ProcessTeamMember[] }>({
        manager: null,
        members: [],
    });

    const appliedDefaultPM = useRef(false);

    useEffect(() => {
        if (!appliedDefaultPM.current && loggedUser && !projectTeam.manager) {
            setProjectTeam(s => ({ ...s, manager: String(loggedUser) }));
            appliedDefaultPM.current = true;
        }
    }, [loggedUser, projectTeam.manager]);

    useEffect(() => {
        if (initialDeviceId == null) {
            return;
        }

        setValidatedObjectType("DEVICE");
        setObjectOption("EXISTING");
        setSelectedDeviceId(String(initialDeviceId));

        dispatch(loadDeviceDetails(String(initialDeviceId)))
            .then(unwrapResult)
            .then((device) => setSelectedDevice(device))
            .catch(() => undefined);
    }, [dispatch, initialDeviceId]);

    useEffect(() => {
        if (processTitleDirty) return;

        if (validatedObjectType === "DEVICE") {
            const name =
                objectOption === "EXISTING" ? selectedDevice?.name : newDeviceName;
            const sn =
                objectOption === "EXISTING" ? selectedDevice?.serialNo : serialNo;

            setProcessTitle(buildProcessTitle(lang, name, sn));
            return;
        }

        if (validatedObjectType === "COMPUTERIZED_SYSTEM") {
            setProcessTitle(buildProcessTitle(computerizedSystem?.name, ""));
            return;
        }
        //todo ROOM
        setProcessTitle(buildProcessTitle("", ""));
    }, [
        processTitleDirty,
        validatedObjectType,
        objectOption,
        selectedDevice?.name,
        selectedDevice?.serialNo,
        newDeviceName,
        serialNo,
        computerizedSystem?.name,
    ]);


    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);
    const handleStepClick = (step: number) => {
        if (step <= activeStep) {
            setActiveStep(step);
            return;
        }
        if (step === activeStep + 1 && canProceedToNext(activeStep)) {
            setActiveStep(step);
        }
    };

    const validateBeforeSave = (): string | null => {
        if (!validatedObjectType)
            return "Wybierz typ walidowanego obiektu.";

        if (!scenario)
            return "Wybierz scenariusz walidacji.";

        const isNewObject = objectOption === "NEW";
        const isExistingObject = !isNewObject && objectOption === "EXISTING";

        if (validatedObjectType === "COMPUTERIZED_SYSTEM") {
            if (!computerizedSystemId) return "Wybierz system.";
        } else if (validatedObjectType === "DEVICE") {
            if (objectOption === "NEW") {
                if (!newDeviceName?.trim()) return "Podaj nazwę nowego obiektu.";
                if (!serialNo?.trim()) return "Podaj numer seryjny.";
                if (!serialValidationOk) return "Numer seryjny jest niepoprawny.";
                if (!structureId) return "Wybierz strukturę dla nowego obiektu.";
            } else if (objectOption === "EXISTING") {
                if (!selectedDeviceId) return "Wybierz urządzenie.";
            } else {
                return "Wybierz tryb: istniejący/nowy dla urządzenia.";
            }
        }


        if (!processNumber?.trim())
            return "Podaj numer procesu.";

        if (!processTitle?.trim())
            return "Podaj tytuł procesu.";

        if (ursMode === "EXISTING" && !selectedUrsId) {
            return "Wskaż istniejący URS";
        }

        if (ursMode === "SKIP" && !skipUrsComment.trim()) {
            return "Podaj powód pominięcia URS.";
        }

        return '';
    };


    const handleSerialNoChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSerialNo(e.target.value);
    };

    const canProceedToNext = (step: number): boolean => {
        switch (step) {
            case 0:
                return !!validatedObjectType;
            case 1:
                return !!scenario;
            case 2:
                if (validatedObjectType === "COMPUTERIZED_SYSTEM") {
                    return !!(computerizedSystemId && computerizedSystemId.trim());
                }

                if (validatedObjectType === "DEVICE") {
                    if (objectOption === "EXISTING") {
                        return !!(selectedDeviceId && selectedDeviceId.trim());
                    }
                    if (objectOption === "NEW") {
                        return !!newDeviceName.trim()
                            && !!serialNo.trim()
                            && serialValidationOk
                            && !!structureId;
                    }
                    return false;
                }

                if (validatedObjectType === "ROOM") {
                    // dostosuj gdy dodasz UI dla pokoju
                    return false;
                }
                return false;

            case 3:
                if (!processNumber || !isProcessNumberValid || !projectTeam.manager)
                    return false;

                return true;
            case 4:
                if (!ursMode)
                    return false;
                if (ursMode === "SKIP") {
                    return !!skipUrsComment.trim();
                }
                if (ursMode === "EXISTING" && !selectedUrsId) {
                    return false;

                }
                return true;
            default:
                return true;
        }
    };

    const handleSave = async () => {
        setSaveError(null);
        const validationError = validateBeforeSave();
        if (validationError) {
            setSaveError(validationError);
            return;
        }

        const resolvedProjectId = projectId == null || projectId === ""
            ? null
            : typeof projectId === "number"
                ? projectId
                : projectId.trim()
                    ? Number(projectId.trim())
                    : null;

        if (resolvedProjectId !== null && Number.isNaN(resolvedProjectId)) {
            setSaveError("Id projektu ma niepoprawny format.");
            return;
        }

        setSaving(true);
        try {
            const isNewObject = objectOption === "NEW";

            const payload: CreateValidationProcessPayload = {
                objectType: validatedObjectType!,
                scenario,
                object:
                    validatedObjectType === "COMPUTERIZED_SYSTEM"
                        ? {
                            mode: "EXISTING",
                            id: computerizedSystemId,
                        }
                        : isNewObject
                            ? {
                                mode: "NEW",
                                name: newDeviceName?.trim() || null,
                                serialNo: serialNo?.trim() || null,
                                structureId: structureId ?? null,
                            }
                            : {
                                mode: "EXISTING",
                                id: selectedDeviceId,
                            },
                process: {
                    number: processNumber?.trim(),
                    title: processTitle?.trim(),
                    projectId: resolvedProjectId,
                    projectManager: projectTeam.manager ?? "",
                    projectTeam: projectTeam.members.map(t => ({ userName: t.userName,
                         role: t.role ,isExternal: t.isExternal ?? false})),
                },
                urs: {
                    mode: ursMode,
                    comment: ursMode === "SKIP" ? skipUrsComment.trim() : "",
                    id: ursMode === "EXISTING" ? (selectedUrsId || null) : null,
                },
            };

            const created = (await dispatch(createValidationProcess(payload)).then(unwrapResult)) as ProjectProcess;
            onSave(created);
        } catch (e: any) {
            setSaveError(e?.message || "Nie udało się zapisać procesu.");
        } finally {
            setSaving(false);
        }
    };


    const renderObjectSummary = () => {
        if (validatedObjectType === "COMPUTERIZED_SYSTEM") {
            return computerizedSystem ? (
                <Stack spacing={0.5} paddingLeft={2}>
                    <Typography variant="body2"><b>Nazwa:</b> {computerizedSystem.name || "—"}</Typography>
                    <Typography variant="body2"><b>Vendor:</b> {computerizedSystem.vendor || "—"}</Typography>
                    <Typography variant="body2"><b>Wersja:</b> {computerizedSystem.softwareVersion || "—"}</Typography>
                </Stack>
            ) : (
                <Typography variant="body2">{computerizedSystemId || "—"}</Typography>
            );
        }
        if (objectOption === "NEW") {
            return (
                <Stack spacing={0.5} paddingLeft={2}>
                    <Typography variant="body2"><b>Nazwa:</b> {newDeviceName || "—"}</Typography>
                    <Typography variant="body2"><b>S/N:</b> {serialNo || "—"}</Typography>
                    <Typography variant="body2">
                        <b>Struktura:</b>{" "}
                        {structureId ? <StructureName structureId={String(structureId)} /> : "—"}
                    </Typography>
                </Stack>
            );
        }

        if (objectOption === "EXISTING") {
            if (selectedDevice) {
                return (
                    <Stack spacing={0.5}>
                        <Typography variant="body2"><b>Nazwa:</b> {selectedDevice.name || "—"}</Typography>
                        <Typography variant="body2"><b>S/N:</b> {selectedDevice.serialNo || "—"}</Typography>
                    </Stack>
                );
            }
            return <Typography variant="body2">{selectedDeviceId || "—"}</Typography>;
        }

        return <Typography variant="body2">—</Typography>;
    };

    const ROLE_LABEL: Record<string, string> = {
        M: "Manager",
        C: "Collaborator",
        O: "Observer",
    };

    const roleToColor = (role?: string) =>
        role === "M" ? "success" : role === "O" ? "info" : "default";

    const renderTeamChips = () => {
        const chips = [
            projectTeam.manager && {
                label: `PM: ${projectTeam.manager}`,
                key: `pm-${projectTeam.manager}`,
                color: "success" as const,
                variant: "outlined"
            },
            ...projectTeam.members.map(m => {
                const roleLabel = m.role ? (ROLE_LABEL[m.role] ?? m.role) : "";
                return {
                    label: `${m.userName}${roleLabel ? ` • ${roleLabel}` : ""}`,
                    key: `m-${m.userName}-${m.role ?? "member"}`,
                    color: roleToColor(m.role),
                    variant: "outlined"

                };
            }),
        ].filter(Boolean) as {
            label: string; key: string;
            color?: "default" | "primary" | "secondary" | "success" | "error" | "info" | "warning",
            variant: "outlined"
        }[];

        if (!chips.length) return <Typography variant="body2">—</Typography>;

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" paddingLeft={2}>
                {chips.map(c => (
                    <Chip key={c.key} label={c.label} size="small" color={c.color ?? "default"} variant={c.variant} />
                ))}
            </Stack>
        );
    };

    const getUrsSummary = () => {
        if (ursMode === "EXISTING") return "Numer: " + selectedUrsDoc?.no || "—";
        if (ursMode === "LATER") return "Dodam później";
        if (ursMode === "SKIP") return "Pominięto";
        return "—";
    };

    const objectIcon = (() => {
        if (validatedObjectType === "DEVICE") return <DevicesIcon fontSize="small" />;
        if (validatedObjectType === "COMPUTERIZED_SYSTEM") return <DnsIcon fontSize="small" />;
        if (validatedObjectType === "ROOM") return <MeetingRoomIcon fontSize="small" />;
        return null;
    })();

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <ValidationProcessStepHeader
                            title={<Localized id="validation-process-object-question">
                                <span>_co walidaowac</span>
                            </Localized>}
                            helpGuid="8f3a9d2e-4e6c-41b2-9f2a-0a6b7f6c1c11"
                        />
                        <Stack direction="row" spacing={2}>
                            <Box
                                onClick={() => setValidatedObjectType("DEVICE")}
                                sx={cardStyle(validatedObjectType === "DEVICE")}
                                role="button"
                                tabIndex={0}
                            >
                                <CheckCircleIcon className="selected-mark" />
                                <DevicesIcon />
                                <Typography className="card-label">
                                    <Localized id="device">
                                        <span>_device</span>
                                    </Localized>
                                </Typography>
                            </Box>
                            <Box
                                onClick={() => !isValidatedObjectLockedToDevice && setValidatedObjectType("COMPUTERIZED_SYSTEM")}
                                sx={cardStyle(validatedObjectType === "COMPUTERIZED_SYSTEM", isValidatedObjectLockedToDevice)}
                                role="button"
                                tabIndex={isValidatedObjectLockedToDevice ? -1 : 0}
                                aria-disabled={isValidatedObjectLockedToDevice}
                            >
                                <CheckCircleIcon className="selected-mark" />
                                <DnsIcon />
                                <Typography className="card-label">
                                    <Localized id="cs-computerized-system">
                                        <span>_computerized-system</span>
                                    </Localized>
                                </Typography>
                            </Box>
                            <Box
                                onClick={() => !isValidatedObjectLockedToDevice && setValidatedObjectType("ROOM")}
                                sx={cardStyle(validatedObjectType === "ROOM", isValidatedObjectLockedToDevice)}
                                role="button"
                                tabIndex={isValidatedObjectLockedToDevice ? -1 : 0}
                                aria-disabled={isValidatedObjectLockedToDevice}
                            >
                                <CheckCircleIcon className="selected-mark" />
                                <MeetingRoomIcon />
                                <Typography className="card-label">
                                    <Localized id="rooms">
                                        <span>_room</span>
                                    </Localized>
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <ValidationProcessStepHeader
                            title={<Localized id="validation-process-scenario-choose">
                                <span>validation-process-scenario-choose</span>
                            </Localized>}
                            helpGuid="8f3a9d2e-4e6c-41b2-9f2a-0a6b7f6c1c12"
                        />
                        <FormControl component="fieldset">
                            <RadioGroup value={scenario} onChange={(e) => setScenario(e.target.value as ValidationScenario)}>
                                <FormControlLabel value="NEW" control={<Radio />} label={<Localized id="validation-process-new-validation">
                                    <span>_new-validation</span>
                                </Localized>} />
                                <FormControlLabel value="REQUAL_PERIODIC" control={<Radio />}
                                    label={<Localized id="validation-process-requal-periodic">
                                        <span>_requal-periodic</span>
                                    </Localized>} />
                                <FormControlLabel value="REQUAL_CHANGE" control={<Radio />} label={<Localized id="validation-process-requal-change">
                                    <span>_requal-change</span>
                                </Localized>} />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                );
            case 2:
                return (
                    <Box display="flex" flexDirection="column" gap={3}>
                        <ValidationProcessStepHeader
                            title={<Localized id="validation-process-validated-object-data">
                                <span>_Wprowadź dane walidowanego obiektu</span>
                            </Localized>}
                            helpGuid="8f3a9d2e-4e6c-41b2-9f2a-0a6b7f6c1c12"
                        />
                        {validatedObjectType === "COMPUTERIZED_SYSTEM" && (
                            <Stack spacing={2} mt={1}>
                                <ComputerizedSystemSelectField
                                    value={computerizedSystemId || null}
                                    onChange={(id) => setComputerizedSystemId(id)}
                                    onSelectSystem={setComputerizedSystem}
                                />
                            </Stack>
                        )}
                        {validatedObjectType === "DEVICE" && (
                            <>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        value={objectOption}
                                        onChange={(e) => {
                                            const nextOption = e.target.value as "EXISTING" | "NEW";
                                            if (isValidatedObjectLockedToDevice && nextOption === "NEW") {
                                                return;
                                            }
                                            setObjectOption(nextOption);
                                        }}
                                    >
                                        <FormControlLabel
                                            value="EXISTING"
                                            control={<Radio />}
                                            label="Zarejestrowany obiekt w systemie"
                                        />
                                        <FormControlLabel
                                            value="NEW"
                                            control={<Radio />}
                                            label="Rejestracja nowego obiektu"
                                            disabled={isValidatedObjectLockedToDevice}
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {objectOption === "EXISTING" && (
                                    <Stack spacing={2} mt={1}>
                                        <DeviceSelectField
                                            value={selectedDeviceId || null}
                                            onChange={(id) => setSelectedDeviceId(id)}
                                            onSelectDevice={setSelectedDevice}
                                            disabled={isValidatedObjectLockedToDevice}
                                        />
                                    </Stack>
                                )}

                                {objectOption === "NEW" && (
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <TextField
                                            label="Nazwa"
                                            value={newDeviceName}
                                            onChange={(e) => setNewDeviceName(e.target.value)}
                                            required
                                            error={!newDeviceName.trim()}
                                            helperText={!newDeviceName.trim() ? "Wymagane" : " "}
                                            fullWidth
                                        />
                                        <DeviceSerialNoField
                                            fullWidth
                                            required
                                            value={serialNo}
                                            onChange={handleSerialNoChange}
                                            label="Numer seryjny"
                                            onValidationResult={setSerialValidationOk}
                                        />
                                        <StructureTreeSelect
                                            defaultStructureId={structureId}
                                            onStructureSelected={handleDeviceTypeIdChange}
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                );
            case 3:
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <ValidationProcessStepHeader
                            title={<Localized id="validation-process-data">
                                <span>_validation-process-data</span>
                            </Localized>}
                            helpGuid="8f3a9d2e-4e6c-41b2-9f2a-0a6b7f6c1c12"
                        />
                        <ProcessNumberField
                            value={processNumber}
                            onChange={(e) => setProcessNumber(e.target.value)}
                            onValidationResult={setIsProcessNumberValid}
                            structureCode={structureCode}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Nazwa procesu"
                            value={processTitle}
                            onChange={(e) => {
                                setProcessTitle(e.target.value);
                                setProcessTitleDirty(true);
                            }}
                            required
                            error={!processTitle.trim()}
                            helperText={!processTitle.trim() ? "Wymagane" : " "}
                            fullWidth
                        />
                        <ValidationTeamEditor
                            manager={projectTeam.manager}
                            members={projectTeam.members}
                            onChange={setProjectTeam}
                        />
                    </Box>
                );
            case 4:
                return (
                    <Stack spacing={3}>
                        <Box>
                            <ValidationProcessStepHeader
                                title={<Localized id="validation-process-urs-question">
                                    <span>_validation-process-urs-question</span>
                                </Localized>}
                                helpGuid="8f3a9d2e-4e6c-41b2-9f2a-0a6b7f6c1c12"
                            />
                            <Stack direction="column" spacing={2}>
                                <Box onClick={() => setUrsMode("EXISTING")} sx={{ ...choiceCard(ursMode === "EXISTING"), width: '100%' }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {ursMode === "EXISTING"
                                            ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                                            : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />
                                        }
                                        <DnsIcon sx={{ color: 'text.secondary' }} />
                                        <Box>
                                            <Typography fontWeight={700} variant="body2">Mam URS</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Wskaż istniejący dokument lub podaj numer.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box onClick={() => setUrsMode("LATER")} sx={{ ...choiceCard(ursMode === "LATER"), width: '100%' }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {ursMode === "LATER"
                                            ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                                            : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />
                                        }
                                        <MeetingRoomIcon sx={{ color: 'text.secondary' }} />
                                        <Box>
                                            <Typography fontWeight={700} variant="body2">Dodam później</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Uzupełnisz URS na późniejszym etapie.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box onClick={() => setUrsMode("SKIP")} sx={{ ...choiceCard(ursMode === "SKIP"), width: '100%' }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {ursMode === "SKIP"
                                                ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                                                : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />
                                            }
                                            <NotInterested sx={{ color: 'text.secondary' }} />
                                            <Typography fontWeight={700} variant="body2">Pomiń</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Kontynuuj bez URS w tym procesie.
                                            </Typography>


                                        </Stack>

                                        {ursMode === "SKIP" && (
                                            <TextField
                                                label="Przyczyna pominięcia URS"
                                                sx={{ marginTop: 2 }}
                                                value={skipUrsComment}
                                                onChange={(e) => setSkipUrsComment(e.target.value)}
                                                required
                                                error={!skipUrsComment.trim()}
                                                helperText={!skipUrsComment.trim() ? "Wymagane" : " "}
                                                fullWidth
                                                margin="dense"
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>

                        </Box>
                        <Collapse in={ursMode === "EXISTING"}>
                            <Stack spacing={2} mt={1}>
                                <DocumentSelectField
                                    documentType="URS"
                                    value={selectedUrsId}
                                    selected={selectedUrsDoc}
                                    onChange={(id, doc) => { setSelectedUrsId(id); setSelectedUrsDoc(doc ?? null); }}
                                />
                            </Stack>
                        </Collapse>

                    </Stack>
                );

            case 5:
                return (
                    <Box>
                        <ValidationProcessStepHeader
                            title={<Localized id="validation-process-summary">
                                <span>_summary</span>
                            </Localized>}
                            helpGuid="8f3a9d2e-4e6c-41b2-9f2a-0a6b7f6c1c99"
                        />
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {objectIcon}
                                <Chip
                                    label={validatedObjectType ? PROCESS_TYPE_LABEL[validatedObjectType] : "Nie wybrano typu"}
                                    size="small"
                                    color={validatedObjectType ? "success" : "default"}
                                    variant={validatedObjectType ? "filled" : "outlined"}
                                />
                                <Chip
                                    label={scenario ? SCENARIO_LABEL[scenario] : "Brak scenariusza"}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                />
                            </Stack>

                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    p: 2,
                                    bgcolor: "background.paper",
                                }}
                            >

                                <Stack spacing={2} divider={<Divider />}>
                                    {/* Sekcja Obiekt */}
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle2">Obiekt walidacji</Typography>
                                            <Button size="small" onClick={() => setActiveStep(2)}>Edytuj</Button>
                                        </Stack>
                                        {renderObjectSummary()}
                                    </Stack>

                                    {/* Sekcja Proces */}
                                    <Stack spacing={1} >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle2">Proces</Typography>
                                            <Button size="small" onClick={() => setActiveStep(3)}>Edytuj</Button>
                                        </Stack>
                                        <Stack spacing={0.5} paddingLeft={2}>
                                            <Typography variant="body2"><b>Numer procesu:</b> {processNumber || "—"}</Typography>
                                            <Typography variant="body2"><b>Zespół:</b></Typography>
                                            {renderTeamChips()}
                                        </Stack>
                                    </Stack>

                                    {/* Sekcja URS */}
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle2">URS</Typography>
                                            <Button size="small" onClick={() => setActiveStep(4)}>Edytuj</Button>
                                        </Stack>
                                        <Typography variant="body2" paddingLeft={2}>{getUrsSummary()}</Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                );
            default:
                return null;
        }
    };

    const stepHint = (index: number): React.ReactNode => {
        switch (index) {
            case 0:
                return validatedObjectType ? PROCESS_TYPE_LABEL[validatedObjectType] : "—";
            case 1:
                return scenario ? SCENARIO_LABEL[scenario] : "—";
            case 2: {
                // COMPUTERIZED_SYSTEM → pokazujemy krótki opis wyboru systemu
                if (validatedObjectType === "COMPUTERIZED_SYSTEM") {
                    if (computerizedSystem) {
                        return (
                            <Box component="span" sx={{ display: "inline-block", maxWidth: 210 }}>
                                {computerizedSystem.name || "—"}
                                {Boolean(computerizedSystem.vendor || computerizedSystem.softwareVersion) && (
                                    <>
                                        <br />
                                        <span style={{ color: "var(--mui-palette-text-secondary)" }}>
                                            {(computerizedSystem.vendor || "—") + " • " + (computerizedSystem.softwareVersion || "—")}
                                        </span>
                                    </>
                                )}
                            </Box>
                        );
                    }
                    return computerizedSystemId ? `ID: ${computerizedSystemId}` : "—";
                }

                // DEVICE → jak było dotąd
                if (objectOption === "NEW") {
                    return (
                        <>
                            S/N: {serialNo?.trim() || "—"}
                            <br />
                            <Box component="span">
                                {structureId ? <StructureName structureId={String(structureId)} /> : "—"}
                            </Box>
                        </>
                    );
                }

                if (objectOption === "EXISTING" && selectedDevice) {
                    const selectedStructureId = selectedDevice.structureId ?? null;
                    return (
                        <>
                            S/N: {selectedDevice.serialNo || "—"}
                            <br />
                            <Box component="span">
                                {selectedStructureId ? <StructureName structureId={String(selectedStructureId)} /> : "—"}
                            </Box>
                        </>
                    );
                }

                return (
                    <Box component="span">
                        S/N: —<br />—
                    </Box>
                );
            }
            case 3:
                return (
                    <>
                        No: {processNumber || "—"}
                        <br />
                        PM: {projectTeam.manager || "—"}
                    </>
                );

            case 4: {
                // pokazywanie skrótu dla kroku URS
                if (ursMode === "EXISTING") {
                    // preferuj numer z wybranego dokumentu, a jeśli brak, pokaż samo ID
                    const no = selectedUrsDoc?.no || selectedUrsId || "—";
                    return `URS: ${no}`;
                }
                if (ursMode === "LATER") return "URS: dodam później";
                if (ursMode === "SKIP") return "URS: pominięto";
                return "URS: —";
            }
            case 5:
                return "Podgląd";
            default:
                return "—";
        }
    };

    const cardStyle = (selected: boolean, disabled = false) => (theme: any) => ({
        position: "relative",
        p: 1.5,
        border: 2,
        borderRadius: 2,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        width: 180,
        textAlign: "center",
        transition: "background-color .15s ease, border-color .15s ease, transform .05s ease",
        borderColor: selected ? theme.palette.success.main : theme.palette.divider,
        bgcolor: selected ? alpha(theme.palette.success.main, 0.08) : theme.palette.background.paper,
        "&:hover": {
            borderColor: disabled ? theme.palette.divider : theme.palette.success.main,
            bgcolor: disabled ? theme.palette.background.paper : selected ? alpha(theme.palette.success.main, 0.12) : theme.palette.action.hover,
        },
        "&:active": { transform: disabled ? "none" : "scale(0.997)" },

        "& .card-label": {
            fontWeight: 700,
            color: selected ? theme.palette.text.primary : theme.palette.text.primary,
        },

        "& .selected-mark": {
            position: "absolute",
            top: 6,
            right: 6,
            color: theme.palette.success.main,
            fontSize: 22,
            pointerEvents: "none",
            display: selected ? "block" : "none",
        },
    });

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        onClose();
    };

    return (
        <Dialog open onClose={handleDialogClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

                <Localized id="validation-process-new">
                    <span>Nowy proces walidacyjny</span>
                </Localized>
                <IconButton onClick={onClose}><Close /></IconButton>
            </DialogTitle>
            <DialogContent>
                <Box display="flex">
                    <Box width={250} bgcolor={theme.palette.grey[100]} p={2}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    <StepLabel
                                        onClick={() => handleStepClick(index)}
                                        sx={{ cursor: "pointer" }}
                                        optional={
                                            <Typography
                                                variant="caption"
                                                sx={{ maxWidth: 210, display: "block", whiteSpace: "pre-line" }} // tu zmiana
                                            >
                                                {stepHint(index)}
                                            </Typography>
                                        }
                                    >{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <Box flexGrow={1} sx={{ marginLeft: 2 }}>{renderStepContent()}</Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.grey[100], justifyContent: "flex-end" }}>
                <Button onClick={onClose}>Anuluj</Button>
                <Button disabled={activeStep === 0} onClick={handleBack}>Wstecz</Button>
                <Button
                    variant="contained"
                    onClick={activeStep === steps.length - 1 ? handleSave : handleNext}
                    sx={{ ml: 2 }}
                    disabled={saving || (!isLastStep && !canProceedToNext(activeStep))}
                >
                    {saving
                        ? <Stack direction="row" gap={1} alignItems="center">
                            <CircularProgress size={18} />
                            <span>Zapisuję…</span>
                        </Stack>
                        : (activeStep === steps.length - 1 ? "Utwórz proces" : "Dalej")}
                </Button>
            </DialogActions>
            <Snackbar
                open={!!saveError}
                autoHideDuration={6000}
                onClose={() => setSaveError(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" onClose={() => setSaveError(null)} variant="filled">
                    {saveError}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!saveSuccess}
                autoHideDuration={3000}
                onClose={() => setSaveSuccess(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSaveSuccess(null)} variant="filled">
                    {saveSuccess}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default ValidationProcessWizard;

